import React, { useEffect, useState } from "react";
import {
    Box,
    Heading,
    Text,
    Checkbox,
    VStack,
    HStack,
    Button,
    useToast,
    Spinner,
    Flex,
    Divider,
    useColorModeValue,
    Input,
    InputGroup,
    InputLeftElement,
    InputRightElement,
    SimpleGrid,
    Card,
    CardBody,
    CardHeader,
    IconButton,
    Avatar,
    Badge,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
} from "@chakra-ui/react";
import { SearchIcon, CheckCircleIcon, SettingsIcon, CloseIcon } from "@chakra-ui/icons";
import api from '../../auth/apiClient';

interface User {
    id: number;
    name: string;
    uuid: string;
    nip: string;
}

interface Category {
    id: number;
    name: string;
    code: string;
}

const CategoryPermissions: React.FC = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [categories, setCategories] = useState<Category[]>([]);
    const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const toast = useToast();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const bg = useColorModeValue("gray.50", "gray.800");
    const cardBg = useColorModeValue("white", "gray.700");
    const borderColor = useColorModeValue("gray.200", "gray.600");
    const hoverBg = useColorModeValue("blue.50", "blue.900");
    const selectedBg = useColorModeValue("blue.100", "blue.800");

    useEffect(() => {
        fetchUsers();
        fetchCategories();
    }, []);

    useEffect(() => {
        if (selectedUser) fetchPermissions(selectedUser.id);
    }, [selectedUser]);

    const fetchUsers = async () => {
        try {
            const res = await api.get('/user/users');
            console.log("Fetched users:", res.data);
            setUsers(res.data);
        } catch (err) {
            toast({ status: "error", title: "Gagal memuat user" });
        }
    };

    const fetchCategories = async () => {
        try {
            const res = await api.get('/inspeksi/category-db');
            setCategories(res.data);
        } catch (err) {
            toast({ status: "error", title: "Gagal memuat kategori" });
        }
    };

    const fetchPermissions = async (userId: number) => {
        setLoading(true);
        try {
            const res = await api.get(`/user/user-category-permissions/${userId}`);
            setSelectedCategoryIds(res.data);
        } catch (err) {
            toast({ status: "error", title: "Gagal memuat izin kategori" });
        } finally {
            setLoading(false);
        }
    };

    const handleCheckboxChange = (categoryId: number) => {
        setSelectedCategoryIds((prev) =>
            prev.includes(categoryId)
                ? prev.filter((id) => id !== categoryId)
                : [...prev, categoryId]
        );
    };

    const handleSave = async () => {
        if (!selectedUser) return;
        setSaving(true);
        try {
            await api.post(`/user/user-category-permissions/${selectedUser.id}`, {
                category_ids: selectedCategoryIds,
            });
            toast({
                status: "success",
                title: "Izin Diperbarui",
                description: "Perubahan izin kategori berhasil disimpan",
                position: "top-right",
                isClosable: true,
            });
        } catch (err) {
            toast({
                status: "error",
                title: "Gagal menyimpan izin",
                description: "Terjadi kesalahan saat menyimpan perubahan",
                position: "top-right",
                isClosable: true,
            });
        } finally {
            setSaving(false);
        }
    };

    const [searchTerm, setSearchTerm] = useState("");

    const filteredUsers = users.filter(
        (user) =>
            user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.uuid.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.nip.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Flex h="100vh" w="100%" overflow="hidden">
            {/* User List Panel */}
            <Box
                w={{ base: "100%", md: "350px" }}
                p={4}
                bg={bg}
                borderRight="1px solid"
                borderColor={borderColor}
                display="flex"
                flexDirection="column"
                overflow="hidden"
            >
                <Heading size="lg" mb={6} fontWeight="semibold" color="blue.600">
                    Manajemen Izin Kategori
                </Heading>

                <InputGroup mb={5}>
                    <InputLeftElement pointerEvents="none">
                        <SearchIcon color="gray.400" />
                    </InputLeftElement>
                    <Input
                        placeholder="Cari nama, UUID, atau NIP"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        borderRadius="lg"
                        bg={cardBg}
                        focusBorderColor="blue.500"
                    />
                    {searchTerm && (
                        <InputRightElement width="40px">
                            <IconButton
                                aria-label="Clear search"
                                icon={<CloseIcon />}
                                size="xs"
                                variant="ghost"
                                onClick={() => setSearchTerm("")}
                            />
                        </InputRightElement>
                    )}
                </InputGroup>

                <Text fontSize="sm" fontWeight="medium" mb={3} color="gray.500">
                    {filteredUsers.length} Pengguna Ditemukan
                </Text>

                <Box flex="1" overflowY="auto" pr={2}>
                    <VStack align="stretch" spacing={3}>
                        {filteredUsers.map((user) => (
                            <Card
                                key={user.id}
                                bg={selectedUser?.id === user.id ? selectedBg : cardBg}
                                borderRadius="lg"
                                border="1px solid"
                                borderColor={borderColor}
                                cursor="pointer"
                                onClick={() => setSelectedUser(user)}
                                _hover={{ bg: hoverBg, borderColor: "blue.300" }}
                                transition="all 0.2s"
                                boxShadow={selectedUser?.id === user.id ? "md" : "sm"}
                            >
                                <CardBody p={3}>
                                    <HStack>
                                        <Avatar name={user.name} size="sm" bg="blue.500" color="white" />
                                        <Box flex="1">
                                            <HStack justify="space-between">
                                                <Text fontWeight="semibold">{user.name}</Text>
                                                {/* <Badge colorScheme="blue" fontSize="xs">NIP: {user.nip}</Badge> */}
                                            </HStack>
                                            <Text fontSize="xs" color="gray.500" mt={1}>
                                                NIP: {user.nip}
                                            </Text>
                                        </Box>
                                    </HStack>
                                </CardBody>
                            </Card>
                        ))}
                    </VStack>
                </Box>
            </Box>

            {/* Permissions Panel */}
            <Box flex="1" p={6} display="flex" flexDirection="column" bg={bg} overflow="hidden">
                {selectedUser ? (
                    <>
                        <Box mb={6}>
                            <HStack justify="space-between" align="center">
                                <Box>
                                    <Heading size="lg" fontWeight="semibold">
                                        Izin Akses Kategori
                                    </Heading>
                                    <Text color="gray.500" mt={1}>
                                        <Badge colorScheme="blue" fontSize="md" ml={1}>
                                            {selectedUser.name} (NIP: {selectedUser.nip})
                                        </Badge>
                                    </Text>
                                </Box>
                                <Button
                                    colorScheme="blue"
                                    onClick={handleSave}
                                    isLoading={saving}
                                    loadingText="Menyimpan..."
                                    leftIcon={<CheckCircleIcon />}
                                    size="md"
                                >
                                    Simpan Perubahan
                                </Button>
                            </HStack>
                            <Divider my={4} borderColor={borderColor} />
                        </Box>

                        {loading ? (
                            <Flex justify="center" align="center" flex="1">
                                <Spinner size="xl" thickness="3px" color="blue.500" />
                            </Flex>
                        ) : (
                            <Box flex="1" overflowY="auto" pr={4}>
                                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                                    {categories.map((cat) => (
                                        <Card
                                            key={cat.id}
                                            bg={cardBg}
                                            border="1px solid"
                                            borderColor={borderColor}
                                            _hover={{ borderColor: "blue.300" }}
                                            transition="border-color 0.2s"
                                        >
                                            <CardBody p={4}>
                                                <Checkbox
                                                    isChecked={selectedCategoryIds.includes(cat.id)}
                                                    onChange={() => handleCheckboxChange(cat.id)}
                                                    colorScheme="blue"
                                                    size="lg"
                                                    width="100%"
                                                >
                                                    <Box ml={2}>
                                                        <Text fontWeight="medium">{cat.name}</Text>
                                                        <Text fontSize="sm" color="gray.500">Kode: {cat.code}</Text>
                                                    </Box>
                                                </Checkbox>
                                            </CardBody>
                                        </Card>
                                    ))}
                                </SimpleGrid>
                            </Box>
                        )}
                    </>
                ) : (
                    <Flex
                        flex="1"
                        direction="column"
                        align="center"
                        justify="center"
                        textAlign="center"
                        p={8}
                    >
                        <Box
                            p={8}
                            bg={cardBg}
                            borderRadius="xl"
                            boxShadow="md"
                            maxW="md"
                            border="1px dashed"
                            borderColor={borderColor}
                        >
                            <SettingsIcon boxSize={10} color="blue.500" mb={4} />
                            <Heading size="md" mb={2} fontWeight="semibold">
                                Pilih Pengguna
                            </Heading>
                            <Text color="gray.500">
                                Silakan pilih pengguna dari daftar di sebelah kiri untuk melihat dan mengatur izin akses kategori inspeksi
                            </Text>
                        </Box>
                    </Flex>
                )}
            </Box>
        </Flex>
    );
};

export default CategoryPermissions;